/**
 * Canvas Panel Component — Renders the visual canvas (A2UI) in a sandboxed iframe.
 *
 * The canvas panel sits beside the chat messages in a split-pane layout.
 * It communicates with the A2UI iframe via postMessage:
 *
 * Parent → Iframe:
 *   { source: "katanoid-parent", type: "a2ui_push", jsonl: "..." }
 *   { source: "katanoid-parent", type: "a2ui_reset" }
 *   { source: "katanoid-parent", type: "eval", javaScript: "..." }
 *
 * Iframe → Parent:
 *   { source: "katanoid-canvas", type: "ready" }
 *   { source: "katanoid-canvas", type: "user_action", payload: { ... } }
 *   { source: "katanoid-canvas", type: "eval_result", result: "..." }
 *   { source: "katanoid-canvas", type: "snapshot_result", dataUrl: "data:..." }
 */

import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

@customElement("katanoid-canvas-panel")
export class KatanoidCanvasPanel extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  /** URL to display in the iframe (if navigating to a web page) */
  @property({ type: String }) url = "";

  /** Whether the canvas is in A2UI mode (vs plain URL mode) */
  @state() private a2uiMode = true;

  /** Whether the iframe has signaled readiness */
  @state() private iframeReady = false;

  /** Queued JSONL commands received before iframe was ready */
  private pendingJsonl: string[] = [];

  private iframeRef: HTMLIFrameElement | null = null;
  private messageHandler: ((e: MessageEvent) => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();

    this.messageHandler = (e: MessageEvent) => {
      const data = e.data;
      if (!data || data.source !== "katanoid-canvas") return;

      switch (data.type) {
        case "ready":
          console.log("[CanvasPanel] iframe ready, pending JSONL count:", this.pendingJsonl.length);
          this.iframeReady = true;
          // Flush any pending JSONL
          for (const jsonl of this.pendingJsonl) {
            console.log("[CanvasPanel] Flushing pending JSONL, length:", jsonl.length);
            this.sendToIframe("a2ui_push", { jsonl });
          }
          this.pendingJsonl = [];
          break;

        case "user_action":
          // Forward user actions up to the app shell
          this.dispatchEvent(
            new CustomEvent("canvas-user-action", {
              detail: data.payload,
              bubbles: true,
              composed: true,
            })
          );
          break;

        case "eval_result":
        case "eval_error":
          console.log("[Canvas]", data.type, data.result ?? data.error);
          break;

        case "snapshot_result":
          if (data.dataUrl) this.downloadSnapshot(data.dataUrl as string);
          break;

        case "snapshot_error":
          console.error("[Canvas] Snapshot failed:", data.error);
          break;
      }
    };
    window.addEventListener("message", this.messageHandler);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
    this.iframeRef = null;
    this.iframeReady = false;
  }

  // ── Public API (called by consumer-app.ts via ref) ──

  /** Push A2UI JSONL content to the canvas */
  pushA2UI(jsonl: string): void {
    console.log("[CanvasPanel] pushA2UI called, iframeReady:", this.iframeReady, "jsonl length:", jsonl.length);
    this.a2uiMode = true;
    if (this.iframeReady) {
      this.sendToIframe("a2ui_push", { jsonl });
    } else {
      console.log("[CanvasPanel] Queuing JSONL — iframe not ready yet");
      this.pendingJsonl.push(jsonl);
      // Ensure the A2UI iframe is loaded
      if (!this.iframeRef) this.requestUpdate();
    }
  }

  /** Reset A2UI state in the canvas */
  reset(): void {
    this.pendingJsonl = [];
    if (this.iframeReady) {
      this.sendToIframe("a2ui_reset", {});
    }
  }

  /** Navigate the canvas to a URL */
  navigate(url: string): void {
    this.a2uiMode = false;
    this.url = url;
    this.iframeReady = false;
    this.requestUpdate();
  }

  /** Evaluate JavaScript in the canvas */
  evalJS(code: string): void {
    if (this.iframeReady) {
      this.sendToIframe("eval", { javaScript: code });
    }
  }

  /** Capture the canvas as a PNG and download it */
  snapshot(): void {
    if (this.iframeReady) {
      this.sendToIframe("snapshot", {});
    }
  }

  // ── Private helpers ──

  private sendToIframe(type: string, data: Record<string, unknown>): void {
    const iframe = this.iframeRef ?? this.querySelector("iframe");
    console.log("[CanvasPanel] sendToIframe:", type, "iframe found:", !!iframe, "contentWindow:", !!iframe?.contentWindow);
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      { source: "katanoid-parent", type, ...data },
      "*"
    );
  }

  protected override render(): TemplateResult {
    const iframeSrc = this.a2uiMode ? "/a2ui/index.html" : this.url;

    return html`
      <div class="canvas-toolbar">
        <span class="canvas-toolbar-title">Canvas</span>
        <div class="canvas-toolbar-actions">
          <button
            class="canvas-toolbar-btn"
            @click=${() => this.snapshot()}
            aria-label="Download snapshot"
            title="Download snapshot"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1.5" y="3.5" width="13" height="10" rx="1.5" stroke="currentColor" stroke-width="1.2"/>
              <circle cx="8" cy="8.5" r="2.5" stroke="currentColor" stroke-width="1.2"/>
              <circle cx="8" cy="8.5" r="1" fill="currentColor"/>
              <rect x="5" y="2" width="6" height="2" rx="0.5" fill="currentColor" opacity="0.5"/>
            </svg>
          </button>
          <button
            class="canvas-toolbar-btn"
            @click=${this.handleClose}
            aria-label="Close canvas"
            title="Close canvas"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
      <iframe
        class="canvas-iframe"
        src=${iframeSrc}
        sandbox="allow-scripts allow-same-origin"
        @load=${this.handleIframeLoad}
      ></iframe>
    `;
  }

  private handleIframeLoad(e: Event): void {
    this.iframeRef = e.target as HTMLIFrameElement;
  }

  private downloadSnapshot(dataUrl: string): void {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `canvas-snapshot-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private handleClose(): void {
    this.dispatchEvent(
      new CustomEvent("canvas-close", { bubbles: true, composed: true })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-canvas-panel": KatanoidCanvasPanel;
  }
}
